import { useEffect, useState } from "react";
import { getErrorMessageForNonStandardAndStandardResponse } from "../util/NetworkErrorUtil";

import {SnackbarShowMessage} from "../context/snackbar-context";

/**
 * Hook to fetch data from an API request and extract the response body.
 * Handles errors by displaying them via an optional snackbar callback.
 *
 * @template T - The type of the response body (defaults to any[])
 * @param requestMethod - The API request function to call
 * @param requestParamsArray - Array of parameters to spread into the request method. The array itself must be a stable reference.
 * @param snackbarShowMessage - Optional callback to display error messages
 * @returns The response body data
 *
 * @example
 *
 * const getBuyersForSupplier = (supplierId: string) => {...} // API request function
 *
 * const buyers: Buyer[] = useGetRequestBody<Buyer[]>(
 *   getBuyersForSupplier,
 *   [supplierId],
 *   showMessage
 * );
 */
const useGetRequestBody = <T = any[]>(
  requestMethod: (...args: any[]) => Promise<{ body: T }>,
  requestParamsArray: any[],
  snackbarShowMessage?: SnackbarShowMessage
): T => {
  const [result, setResult] = useState<T>([] as T);

  useEffect(() => {
    (async () => {
      try {
        setResult((await requestMethod(...requestParamsArray)).body);
      } catch (err) {
        if (snackbarShowMessage) {
          snackbarShowMessage(
            getErrorMessageForNonStandardAndStandardResponse(err),
            "error",
            15000
          );
        }
      }
    })();
  }, [snackbarShowMessage, requestMethod, requestParamsArray]);

  return result;
};

export default useGetRequestBody;
